import { useState, useMemo, useEffect } from "react";
import { P, FF } from "../../styles/tokens";
import { fmt } from "../../utils/format";
import { catIcon, catColor, CATS_LIST } from "../../constants/categories";
import { isFijoVencido, isFijoPorVencer } from "../../utils/gastos";
import { useFinancial } from "../../contexts/FinancialContext";
import { useNavigation } from "../../contexts/NavigationContext";
import { useUI } from "../../contexts/UIContext";
import { isRecurrenteGhost } from "../../hooks/useMonthData";

const TIPO_ICON = { efectivo: "💵", "débito": "💳", "crédito": "💳", vales: "🎫", ahorro: "🐿️" };

export default function MovimientosView() {
  const { month, cuentas, transferencias, creditoIds, year } = useFinancial();
  const { activeMonth, setSection } = useNavigation();

  const [tab, setTab] = useState("todos");
  const [search, setSearch] = useState("");
  const [catFilters, setCatFilters] = useState(new Set());
  const [cuentaFilters, setCuentaFilters] = useState(new Set());
  const [showFilters, setShowFilters] = useState(false);

  // Al entrar a movimientos: scroll para ocultar el hero y dar protagonismo al buscador.
  // 160px es suficiente para colapsar el hero completo (que mide ~150px) y activar
  // la barra compacta sticky del MobileLayout.
  useEffect(() => {
    window.scrollTo({ top: 160, behavior: "auto" });
  }, []);

  const toggleFilter = (set, setter, val) => {
    const next = new Set(set);
    if (next.has(val)) next.delete(val); else next.add(val);
    setter(next);
  };

  const yr = new Date().getFullYear();
  const cuentaName = (id) => cuentas.find(c => c.id === id);

  // Construir todas las transacciones con fecha
  const allTx = useMemo(() => {
    const txs = [];

    month.ingresos.filter(i => i.monto > 0 && !isRecurrenteGhost(i)).forEach(i => {
      txs.push({
        id: i.id + "i", name: i.concepto, amt: i.monto, type: "ingreso",
        cat: null, cuentaId: i.cuentaId, fecha: i.fecha || null,
        icon: "💵", iconBg: P.ingresos.pastel,
      });
    });

    month.fijos.filter(f => f.monto > 0 && f.pagado && !String(f.id).startsWith("rec_")).forEach(f => {
      txs.push({
        id: f.id, name: f.concepto, amt: f.pagado && f.montoPagado != null ? Number(f.montoPagado) : Number(f.monto), type: "gasto",
        cat: "Fijo", cuentaId: f.cuentaId,
        fecha: f.fechaLimite ? `${yr}-${String(activeMonth + 1).padStart(2, "0")}-${String(f.fechaLimite).padStart(2, "0")}` : null,
        icon: "🏠", iconBg: P.fijos.pastel,
        pagado: f.pagado,
        badge: f.pagado ? "✓ Pagado" : isFijoVencido(f, activeMonth, year) ? "⚠ Vencido" : isFijoPorVencer(f, activeMonth, year) ? "⏰ Hoy" : "Pendiente",
        badgeToken: f.pagado ? P.ingresos : isFijoVencido(f, activeMonth, year) ? P.danger : isFijoPorVencer(f, activeMonth, year) ? P.warn : null,
      });
    });

    month.extras.filter(e => e.monto > 0 && !isRecurrenteGhost(e)).forEach(e => {
      txs.push({
        id: e.id + "e", name: e.concepto, amt: e.monto, type: "gasto",
        cat: e.cat || "Otros", cuentaId: e.cuentaId, fecha: e.fecha || null,
        icon: catIcon(e.cat), iconBg: `${catColor(e.cat)}18`,
      });
    });

    (transferencias || []).filter(t => {
      if (!t.created_at) return false;
      const d = new Date(t.created_at);
      return d.getMonth() === activeMonth && d.getFullYear() === yr;
    }).forEach(t => {
      txs.push({
        id: t.id + "t", name: t.concepto || "Transferencia", amt: t.monto, type: "retiro",
        cat: null, cuentaId: t.cuenta_origen, cuentaDestinoId: t.cuenta_destino,
        fecha: t.created_at ? t.created_at.slice(0, 10) : null,
        icon: "🔄", iconBg: "#6366F118",
      });
    });

    return txs;
  }, [month, transferencias, activeMonth, yr, cuentas]);

  // Filtrar
  const filtered = useMemo(() => {
    let result = allTx;
    if (search.trim()) {
      const q = search.toLowerCase().trim();
      result = result.filter(t => t.name?.toLowerCase().includes(q));
    }
    if (tab === "gastos") result = result.filter(t => t.type === "gasto");
    else if (tab === "ingresos") result = result.filter(t => t.type === "ingreso");
    else if (tab === "retiros") result = result.filter(t => t.type === "retiro");
    if (catFilters.size > 0) result = result.filter(t => catFilters.has(t.cat));
    if (cuentaFilters.size > 0) result = result.filter(t => cuentaFilters.has(t.cuentaId) || cuentaFilters.has(t.cuentaDestinoId));
    return result;
  }, [allTx, tab, catFilters, cuentaFilters, search]);

  // Ordenar por fecha (reciente primero)
  const sorted = [...filtered].sort((a, b) => {
    if (!a.fecha && !b.fecha) return 0;
    if (!a.fecha) return 1;
    if (!b.fecha) return -1;
    return b.fecha.localeCompare(a.fecha);
  });

  // Agrupar por fecha
  const groups = [];
  let lastDate = null;
  sorted.forEach(tx => {
    const dk = tx.fecha || "_sin_fecha";
    if (dk !== lastDate) { groups.push({ key: dk, items: [] }); lastDate = dk; }
    groups[groups.length - 1].items.push(tx);
  });

  const formatDate = (d) => {
    if (d === "_sin_fecha") return "Sin fecha";
    const date = new Date(d + "T12:00:00");
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const target = new Date(date);
    target.setHours(0, 0, 0, 0);
    const diffDays = Math.round((today - target) / 86400000);
    if (diffDays === 0) return "Hoy";
    if (diffDays === 1) return "Ayer";
    return `${date.getDate()} de ${date.toLocaleString("es-MX", { month: "long" })}`;
  };

  // Categorías con gastos para los filtros
  const activeCats = [...new Set(allTx.filter(t => t.cat && t.cat !== "Fijo").map(t => t.cat))];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div style={{ fontSize: 18, fontWeight: 900, color: P.text, fontFamily: FF }}>Movimientos</div>
        <div style={{ fontSize: 12, color: P.textSm, fontFamily: FF }}>{sorted.length} movimiento{sorted.length !== 1 ? "s" : ""}</div>
      </div>

      {/* Buscador */}
      <div style={{ position: "relative" }}>
        <span style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", fontSize: 14, color: P.textXs, pointerEvents: "none" }}>🔍</span>
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Buscar por concepto..."
          style={{ width: "100%", padding: "10px 12px 10px 36px", borderRadius: 12, border: `1.5px solid ${search ? P.ingresos.tint : P.border}`, background: search ? P.ingresos.pastel : P.surface, color: P.text, fontSize: 13, fontFamily: FF, fontWeight: 500, outline: "none", boxSizing: "border-box" }}
        />
        {search && (
          <button type="button" onClick={() => setSearch("")} style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", color: P.textXs, fontSize: 16, cursor: "pointer", padding: 4 }}>✕</button>
        )}
      </div>

      {/* Tabs tipo */}
      <div style={{ display: "flex", gap: 6 }}>
        {[
          { key: "todos", label: "Todos" },
          { key: "gastos", label: "Gastos" },
          { key: "ingresos", label: "Ingresos" },
          { key: "retiros", label: "Retiros" },
        ].map(t => {
          const active = tab === t.key;
          return (
            <button type="button" key={t.key} onClick={() => setTab(t.key)} style={{
              padding: "6px 14px", borderRadius: 99, border: active ? "none" : `1.5px solid ${P.border}`,
              background: active ? "#1E293B" : P.surface, color: active ? "#fff" : P.textSm,
              fontSize: 12, fontWeight: 700, fontFamily: FF, cursor: "pointer",
            }}>
              {t.label}
            </button>
          );
        })}
      </div>

      {/* Filtros avanzados toggle */}
      {(() => {
        const activeCount = catFilters.size + cuentaFilters.size;
        return (
        <button type="button" onClick={() => setShowFilters(!showFilters)} style={{
          display: "flex", alignItems: "center", gap: 6, padding: "6px 12px", borderRadius: 10,
          border: `1px solid ${showFilters || activeCount > 0 ? P.ingresos.base : P.border}`,
          background: showFilters ? P.ingresos.pastel : P.surface, color: showFilters ? P.ingresos.base : P.textSm,
          fontFamily: FF, fontSize: 11, fontWeight: 600, cursor: "pointer", alignSelf: "flex-start",
        }}>
          ⚙ Filtros{activeCount > 0 && ` (${activeCount})`}
        </button>
        );
      })()}

      {showFilters && (
        <div style={{ background: P.bg, borderRadius: 12, padding: 12, border: `1px solid ${P.border}` }}>
          {/* Categoría */}
          <div style={{ fontSize: 10, fontWeight: 700, color: P.textXs, fontFamily: FF, marginBottom: 6, textTransform: "uppercase" }}>Categoría</div>
          <div style={{ display: "flex", gap: 5, flexWrap: "wrap", marginBottom: 12 }}>
            <button type="button" onClick={() => setCatFilters(new Set())} style={{
              padding: "4px 10px", borderRadius: 8, border: `1px solid ${catFilters.size === 0 ? P.ingresos.base : P.border}`,
              background: catFilters.size === 0 ? P.ingresos.pastel : P.surface, color: catFilters.size === 0 ? P.ingresos.base : P.textXs,
              fontSize: 10, fontWeight: 700, fontFamily: FF, cursor: "pointer",
            }}>Todas</button>
            {activeCats.map(c => {
              const active = catFilters.has(c);
              return (
              <button type="button" key={c} onClick={() => toggleFilter(catFilters, setCatFilters, c)} style={{
                padding: "4px 10px", borderRadius: 8,
                border: `1px solid ${active ? catColor(c) : P.border}`,
                background: active ? `${catColor(c)}18` : P.surface,
                color: active ? catColor(c) : P.textXs,
                fontSize: 10, fontWeight: 700, fontFamily: FF, cursor: "pointer",
              }}>{catIcon(c)} {c}</button>
              );
            })}
          </div>

          {/* Cuenta */}
          <div style={{ fontSize: 10, fontWeight: 700, color: P.textXs, fontFamily: FF, marginBottom: 6, textTransform: "uppercase" }}>Cuenta</div>
          <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
            <button type="button" onClick={() => setCuentaFilters(new Set())} style={{
              padding: "4px 10px", borderRadius: 8, border: `1px solid ${cuentaFilters.size === 0 ? P.ingresos.base : P.border}`,
              background: cuentaFilters.size === 0 ? P.ingresos.pastel : P.surface, color: cuentaFilters.size === 0 ? P.ingresos.base : P.textXs,
              fontSize: 10, fontWeight: 700, fontFamily: FF, cursor: "pointer",
            }}>Todas</button>
            {cuentas.map(c => {
              const active = cuentaFilters.has(c.id);
              return (
              <button type="button" key={c.id} onClick={() => toggleFilter(cuentaFilters, setCuentaFilters, c.id)} style={{
                padding: "4px 10px", borderRadius: 8,
                border: `1px solid ${active ? P.ingresos.base : P.border}`,
                background: active ? P.ingresos.pastel : P.surface,
                color: active ? P.ingresos.base : P.textXs,
                fontSize: 10, fontWeight: 700, fontFamily: FF, cursor: "pointer",
              }}>{TIPO_ICON[c.tipo] || "💳"} {c.nombre}</button>
              );
            })}
          </div>
        </div>
      )}

      {/* Lista agrupada por fecha */}
      {sorted.length === 0 ? (
        allTx.length === 0 ? (
          // Empty state sin datos en absoluto
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", padding: "48px 24px", textAlign: "center", background: P.surface, borderRadius: 16, border: `1.5px solid ${P.border}` }}>
            <div style={{ fontSize: 40, marginBottom: 10 }}>🐿️</div>
            <div style={{ fontSize: 14, fontWeight: 700, color: P.text, fontFamily: FF, marginBottom: 6 }}>Aún no tienes movimientos este mes.</div>
            <div style={{ fontSize: 12, color: P.textSm, fontFamily: FF, marginBottom: 16 }}>¡Empieza registrando un gasto!</div>
            <button onClick={() => setSection("editar")} style={{ padding: "9px 22px", borderRadius: 12, border: "none", background: "#143D2E", color: "#fff", fontSize: 12, fontWeight: 700, fontFamily: FF, cursor: "pointer" }}>
              Registrar gasto
            </button>
          </div>
        ) : (
          // Empty state con filtros activos
          <div style={{ textAlign: "center", padding: "40px 0", background: P.surface, borderRadius: 16, border: `1.5px solid ${P.border}` }}>
            <div style={{ fontSize: 28, marginBottom: 8 }}>🌱</div>
            <div style={{ fontSize: 13, color: P.textXs, fontFamily: FF }}>No hay movimientos con estos filtros</div>
          </div>
        )
      ) : (
        groups.map(group => (
          <div key={group.key}>
            {/* Date header */}
            <div style={{ fontSize: 12, fontWeight: 700, color: P.textSm, fontFamily: FF, marginBottom: 8, textTransform: "capitalize" }}>
              {formatDate(group.key)}
            </div>
            <div style={{ background: P.surface, borderRadius: 14, border: `1.5px solid ${P.border}`, overflow: "hidden" }}>
              {group.items.map((tx, i) => {
                const cuenta = cuentaName(tx.cuentaId);
                const isTDC = tx.cuentaId && creditoIds.has(tx.cuentaId);
                return (
                  <div key={tx.id} style={{ padding: "12px 16px", borderBottom: i < group.items.length - 1 ? `1px solid ${P.border}` : "none" }}>
                    {/* Fila principal: icono + nombre + monto */}
                    <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                      <div style={{ width: 40, height: 40, borderRadius: 12, background: tx.iconBg, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 17, flexShrink: 0 }}>{tx.icon}</div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 15, fontWeight: 700, color: P.text, fontFamily: FF, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{tx.name}</div>
                      </div>
                      <div style={{ fontSize: 16, fontWeight: 900, fontFamily: FF, fontVariantNumeric: "tabular-nums", flexShrink: 0,
                        color: tx.type === "ingreso" ? P.ingresos.base : tx.type === "retiro" ? "#6366F1" : P.text
                      }}>
                        {tx.type === "ingreso" ? "+" : tx.type === "retiro" ? "" : "-"}{fmt(Math.abs(tx.amt))}
                      </div>
                    </div>
                    {/* Bubbles: categoría + cuenta */}
                    <div style={{ display: "flex", gap: 6, marginTop: 8, paddingLeft: 52, flexWrap: "wrap" }}>
                      {tx.cat && (
                        <span style={{ fontSize: 10, fontWeight: 600, padding: "2px 8px", borderRadius: 99, background: `${catColor(tx.cat)}18`, color: catColor(tx.cat), border: `1px solid ${catColor(tx.cat)}30`, fontFamily: FF }}>
                          {catIcon(tx.cat)} {tx.cat}
                        </span>
                      )}
                      {cuenta && (
                        <span style={{ fontSize: 10, fontWeight: 600, padding: "2px 8px", borderRadius: 99, background: isTDC ? "#EDE9FE" : P.ingresos.pastel, color: isTDC ? "#7C3AED" : P.ingresos.base, border: `1px solid ${isTDC ? "#C4B5FD" : P.ingresos.tint}`, fontFamily: FF }}>
                          {TIPO_ICON[cuenta.tipo] || "💳"} {cuenta.nombre}
                        </span>
                      )}
                      {tx.type === "retiro" && tx.cuentaDestinoId && (() => {
                        const dest = cuentaName(tx.cuentaDestinoId);
                        return dest ? (
                          <span style={{ fontSize: 10, fontWeight: 600, padding: "2px 8px", borderRadius: 99, background: P.bg, color: P.textSm, border: `1px solid ${P.border}`, fontFamily: FF }}>
                            → {TIPO_ICON[dest.tipo] || "💳"} {dest.nombre}
                          </span>
                        ) : null;
                      })()}
                      {tx.badgeToken && (
                        <span style={{ fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 99, background: tx.badgeToken.pastel, color: tx.badgeToken.text, border: `1px solid ${tx.badgeToken.tint}`, fontFamily: FF }}>
                          {tx.badge}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))
      )}
    </div>
  );
}
